import moment from "moment";
import React, { Fragment, useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import ReactTable from "react-table";
import { Button, Row } from "reactstrap";
import { Colxx } from "../../../components/common/CustomBootstrap";
import StatCard from "../../../components/dashboard/StatCard";
import DataTablePagination from "../../../components/DatatablePagination";
import { functions } from "../../../helpers/Firebase";
import { createNotification } from "../../../helpers/Utils";
import Customer from "./components/Customer";
import './crmstyles.css';

const WalletLogs = () => {
  const [groupedData, setGroupedData] = useState([])
  const [filteredData, setFilteredData] = useState([])
  const [loading, setLoading] = useState(true)
  const [startDate, setStartDate] = useState(new Date(new Date().setDate(new Date().getDate() - 7)))
  const [endDate, setEndDate] = useState(new Date())
  const [selectedCustomer, setSelectedCustomer] = useState(null)
  const [dateRangeError, setDateRangeError] = useState('')
  const notificationFiredRef = React.useRef(false)
  const [analytics, setAnalytics] = useState({
    total: 0,
    totalCredit: 0,
    totalCashback: 0,
    totalVouchers: 0,
    totalCompensation: 0,
    redeemedCredit: 0,
    redeemedCashback: 0,
    redeemedVouchers: 0,
    redeemedCompensation: 0,
    redeemedBogoAmount: 0,
    totalTransferred: 0,
    transferredCount: 0,
    totalTransactions: 0,
    totalUsers: 0
  })

  const handleViewCustomer = (customerId) => {
    // Find customer data from grouped data
    const customerData = groupedData.find(item => item.customerId === customerId)
    if (customerData) {
      setSelectedCustomer({
        id: customerId,
        key: customerId,
        name: customerData.customerName,
        number: customerData.customerNumber,
        email: customerData.customerEmail,
        phoneNumber: customerData.customerNumber,
        walletLog: customerData.walletLogs || [],
        orders: [], // Empty array for orders - Customer component will handle this
        ordersPlaced: 0,
        revenue: 0,
        firstOrder: customerData.firstLogDate || new Date().getTime(),
        lastOrder: customerData.lastLogDate || new Date().getTime()
      })
    }
  }

  const validateDateRange = (start, end, showNotification = true) => {
    if (!start || !end) return true

    const diffTime = Math.abs(end - start)
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    const maxDays = 30 // 1 month limit

    if (diffDays > maxDays) {
      setDateRangeError(`Date range cannot exceed ${maxDays} days. Please select a smaller range.`)

      // Only show notification once per validation cycle
      if (showNotification && !notificationFiredRef.current) {
        notificationFiredRef.current = true
        createNotification(
          'warning',
          `Date range cannot exceed ${maxDays} days. Please select a smaller range.`,
          'Date Range Limit Exceeded'
        )
        // Reset the ref after a short delay to allow re-notification if user tries again
        setTimeout(() => {
          notificationFiredRef.current = false
        }, 1000)
      }
      return false
    } else {
      setDateRangeError('')
      notificationFiredRef.current = false
      return true
    }
  }

  const handleDateRangeChange = (dates) => {
    const [start, end] = dates
    
    // Update start date immediately when selected
    if (start) {
      setStartDate(start)
      // Clear end date if start date changes (user is selecting new range)
      if (!end) {
        setEndDate(null)
      }
    }
    
    // Update end date when selected
    if (end) {
      // Validate range when both dates are selected
      if (start) {
        const isValid = validateDateRange(start, end, true)
        if (isValid) {
          setEndDate(end)
          // Data will be fetched automatically via useEffect when both dates are set
        } else {
          // If invalid, don't update end date but keep start date
          return
        }
      } else {
        // If no start date, don't set end date
        return
      }
    } else if (start) {
      // If only start date is selected, clear error and end date
      setDateRangeError('')
      setEndDate(null)
    }
  }

  const getDateRangeDisplay = () => {
    if (startDate && endDate) {
      return `${moment(startDate).format('MMM DD, YYYY')} - ${moment(endDate).format('MMM DD, YYYY')}`
    }
    if (startDate) {
      return `${moment(startDate).format('MMM DD, YYYY')} - ...`
    }
    return 'Select date range'
  }

  const exportWalletLogsCSV = () => {
    if (!groupedData || groupedData.length === 0) {
      createNotification('warning', 'No wallet logs data to export', 'Export Failed')
      return
    }

    const csvRows = []
    
    // Define CSV headers matching the table columns
    const headers = [
      'Customer Name',
      'Customer Phone',
      'Customer Email',
      'Wallet Logs Count',
      'Wallet Total',
      'Total Credit',
      'Total Cashback',
      'Total Vouchers',
      'Total Compensation',
      'Redeemed Credit',
      'Redeemed Cashback',
      'Redeemed Vouchers',
      'Redeemed Compensation',
      'Redeemed BOGO Amount (AED)',
      'Total Transferred',
      'Transferred Count'
    ]
    
    // Add header row to CSV
    csvRows.push(headers.join(','))

    // Helper function to sanitize CSV fields
    const sanitizeField = (field) => {
      if (field === null || field === undefined || field === '') return '--'
      const str = String(field)
      // If field contains comma, quote, or newline, wrap in quotes and escape existing quotes
      if (str.includes(',') || str.includes('"') || str.includes('\n') || str.includes('\r')) {
        return `"${str.replace(/"/g, '""')}"`
      }
      return str
    }

    // Loop through each grouped user (one row per user, matching table structure)
    for (const userGroup of groupedData) {
      const {
        customerName,
        customerNumber,
        customerEmail,
        walletLogsCount,
        analytics
      } = userGroup

      const row = [
        sanitizeField(customerName || '--'),
        sanitizeField(customerNumber || '--'),
        sanitizeField(customerEmail || '--'),
        walletLogsCount || 0,
        analytics?.total ? Number(analytics.total).toFixed(2) : '0.00',
        analytics?.totalCredit ? Number(analytics.totalCredit).toFixed(2) : '0.00',
        analytics?.totalCashback ? Number(analytics.totalCashback).toFixed(2) : '0.00',
        analytics?.totalVouchers ? Number(analytics.totalVouchers).toFixed(2) : '0.00',
        analytics?.totalCompensation ? Number(analytics.totalCompensation).toFixed(2) : '0.00',
        analytics?.redeemedCredit ? Number(analytics.redeemedCredit).toFixed(2) : '0.00',
        analytics?.redeemedCashback ? Number(analytics.redeemedCashback).toFixed(2) : '0.00',
        analytics?.redeemedVouchers ? Number(analytics.redeemedVouchers).toFixed(2) : '0.00',
        analytics?.redeemedCompensation ? Number(analytics.redeemedCompensation).toFixed(2) : '0.00',
        analytics?.redeemedBogoAmount ? Number(analytics.redeemedBogoAmount).toFixed(2) : '0.00',
        analytics?.totalTransferred ? Number(analytics.totalTransferred).toFixed(2) : '0.00',
        analytics?.transferredCount || 0
      ]
      csvRows.push(row.join(','))
    }

    // Combine all rows into a single CSV string
    const csvString = csvRows.join('\n')

    // Create a download link for the CSV file
    const link = document.createElement('a')
    link.href = 'data:text/csv;charset=utf-8,' + encodeURIComponent(csvString)
    const dateRange = startDate && endDate 
      ? `${moment(startDate).format('YYYY-MM-DD')}_to_${moment(endDate).format('YYYY-MM-DD')}`
      : moment().format('YYYY-MM-DD')
    link.download = `wallet_logs_${dateRange}.csv`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    
    createNotification('success', 'Wallet logs exported successfully', 'Export Complete')
  }

  const dataTableColumns = () => [
    {
      Header: "Customer",
      accessor: "customerName",
      sortable: false,
      width: 200,
      getHeaderProps: () => ({
        title: "Customer"
      }),
      Cell: props => <div className="d-flex flex-row align-items-center">
        <p className="list-item-heading">{props.value || '--'} <br />{props.original.customerNumber || '--'}</p><br />
      </div>
    },
    {
      Header: "Wallet Logs Count",
      accessor: "walletLogsCount",
      sortable: false,
      getHeaderProps: () => ({
        title: "Wallet Logs Count"
      }),
      Cell: props => <p className="list-item-heading">{props.value || 0}</p>
    },
    {
      Header: "Wallet Total",
      accessor: "analytics.total",
      sortable: false,
      getHeaderProps: () => ({
        title: "Wallet Total"
      }),
      Cell: props => <p className="list-item-heading">{"AED " + (props.value || 0)?.toFixed(2)}</p>
    },
    {
      Header: "Total Credit",
      accessor: "analytics.totalCredit",
      sortable: false,
      getHeaderProps: () => ({
        title: "Total Credit"
      }),
      Cell: props => <p className="list-item-heading">{"AED " + (props.value || 0)?.toFixed(2)}</p>
    },
    {
      Header: "Total Cashback",
      accessor: "analytics.totalCashback",
      sortable: false,
      getHeaderProps: () => ({
        title: "Total Cashback"
      }),
      Cell: props => <p className="list-item-heading">{"AED " + (props.value || 0)?.toFixed(2)}</p>
    },
    {
      Header: "Total Vouchers",
      accessor: "analytics.totalVouchers",
      sortable: false,
      getHeaderProps: () => ({
        title: "Total Vouchers"
      }),
      Cell: props => <p className="list-item-heading">{"AED " + (props.value || 0)?.toFixed(2)}</p>
    },
    {
      Header: "Total Compensation",
      accessor: "analytics.totalCompensation",
      sortable: false,
      getHeaderProps: () => ({
        title: "Total Compensation"
      }),
      Cell: props => <p className="list-item-heading">{"AED " + (props.value || 0)?.toFixed(2)}</p>
    },
    {
      Header: "Redeemed Credit",
      accessor: "analytics.redeemedCredit",
      sortable: false,
      getHeaderProps: () => ({
        title: "Redeemed Credit"
      }),
      Cell: props => <p className="list-item-heading" style={{ color: '#e74c3c' }}>{"AED " + (props.value || 0)?.toFixed(2)}</p>
    },
    {
      Header: "Redeemed Cashback",
      accessor: "analytics.redeemedCashback",
      sortable: false,
      getHeaderProps: () => ({
        title: "Redeemed Cashback"
      }),
      Cell: props => <p className="list-item-heading" style={{ color: '#e74c3c' }}>{"AED " + (props.value || 0)?.toFixed(2)}</p>
    },
    {
      Header: "Redeemed Vouchers",
      accessor: "analytics.redeemedVouchers",
      sortable: false,
      getHeaderProps: () => ({
        title: "Redeemed Vouchers"
      }),
      Cell: props => <p className="list-item-heading" style={{ color: '#e74c3c' }}>{"AED " + (props.value || 0)?.toFixed(2)}</p>
    },
    {
      Header: "Redeemed Compensation",
      accessor: "analytics.redeemedCompensation",
      sortable: false,
      getHeaderProps: () => ({
        title: "Redeemed Compensation"
      }),
      Cell: props => <p className="list-item-heading" style={{ color: '#e74c3c' }}>{"AED " + (props.value || 0)?.toFixed(2)}</p>
    },
    {
      Header: "Redeemed BOGO Amount",
      accessor: "analytics.redeemedBogoAmount",
      sortable: false,
      getHeaderProps: () => ({
        title: "Redeemed BOGO Amount (AED)"
      }),
      Cell: props => <p className="list-item-heading" style={{ color: '#e74c3c' }}>{"AED " + (props.value || 0)?.toFixed(2)}</p>
    },
    {
      Header: "Total Transferred",
      accessor: "analytics.totalTransferred",
      sortable: false,
      getHeaderProps: () => ({
        title: "Total Transferred (to Bank)"
      }),
      Cell: props => <p className="list-item-heading" style={{ color: '#2ecc71' }}>{"AED " + (props.value || 0)?.toFixed(2)}</p>
    },
    // {
    //   Header: "Transferred Count",
    //   accessor: "analytics.transferredCount",
    //   sortable: false,
    //   getHeaderProps: () => ({
    //     title: "Transferred Count"
    //   }),
    //   Cell: props => <p className="list-item-heading" style={{ color: '#27ae60' }}>{props.value || 0}</p>
    // },
    {
      Header: "Action",
      accessor: "customerId",
      sortable: false,
      getHeaderProps: () => ({
        title: "Action"
      }),
      Cell: props => (
        <Button
          color="primary"
          size="sm"
          onClick={() => handleViewCustomer(props.value)}
        >
          View Customer
        </Button>
      )
    },
  ];

  const fetchWalletLogs = async () => {
    setLoading(true)
    setGroupedData([])
    setFilteredData([])

    try {
      const getWalletLogs = functions().httpsCallable('getWalletLogsGroupedByUser');
      const result = await getWalletLogs({
        startDate: startDate,
        endDate: endDate
      });

      if (result.data.error) {
        console.error('Error from function:', result.data.error);
        setGroupedData([])
        setFilteredData([])
        setAnalytics({
          total: 0,
          totalCredit: 0,
          totalCashback: 0,
          totalVouchers: 0,
          totalCompensation: 0,
          redeemedCredit: 0,
          redeemedCashback: 0,
          redeemedVouchers: 0,
          redeemedCompensation: 0,
          redeemedBogoAmount: 0,
          totalTransferred: 0,
          transferredCount: 0,
          totalTransactions: 0,
          totalUsers: 0
        })
      } else {
        const { data, analytics: analyticsData } = result.data;
        setGroupedData(data || [])
        setFilteredData(data || [])
        setAnalytics({
          total: Number(analyticsData.total).toFixed(2) || 0,
          totalCredit: Number(analyticsData.totalCredit).toFixed(2) || 0,
          totalCashback: Number(analyticsData.totalCashback).toFixed(2) || 0,
          totalVouchers: Number(analyticsData.totalVouchers).toFixed(2) || 0,
          totalCompensation: Number(analyticsData.totalCompensation).toFixed(2) || 0,
          redeemedCredit: Number(analyticsData.redeemedCredit).toFixed(2) || 0,
          redeemedCashback: Number(analyticsData.redeemedCashback).toFixed(2) || 0,
          redeemedVouchers: Number(analyticsData.redeemedVouchers).toFixed(2) || 0,
          redeemedCompensation: Number(analyticsData.redeemedCompensation).toFixed(2) || 0,
          redeemedBogoAmount: Number(analyticsData.redeemedBogoAmount).toFixed(2) || 0,
          totalTransferred: Number(analyticsData.totalTransferred).toFixed(2) || 0,
          transferredCount: Number(analyticsData.transferredCount) || 0,
          totalTransactions: Number(analyticsData.totalTransactions) || 0,
          totalUsers: Number(analyticsData.totalUsers) || 0
        })
      }
    } catch (error) {
      console.error('Error fetching wallet logs:', error)
      setGroupedData([])
      setFilteredData([])
      setAnalytics({
        total: 0,
        totalCredit: 0,
        totalCashback: 0,
        totalVouchers: 0,
        totalCompensation: 0,
        redeemedCredit: 0,
        redeemedCashback: 0,
        redeemedVouchers: 0,
        redeemedCompensation: 0,
        redeemedBogoAmount: 0,
        totalTransferred: 0,
        transferredCount: 0,
        totalTransactions: 0,
        totalUsers: 0
      })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    // Only fetch if both dates are selected and range is valid
    if (startDate && endDate) {
      // Don't show notification in useEffect, only validate
      const isValid = validateDateRange(startDate, endDate, false)
      if (isValid) {
        fetchWalletLogs()
      }
    }
    // Only run when both dates change (not when only one is selected)
    // eslint-disable-next-line
  }, [startDate, endDate])

  return (
    <Fragment>
      {(loading) && <div className='loading'></div>}
      <div className="mb-3">
        <Row>
          <Colxx xxs="12" md="6">
            <h1 className="mb-2 p-0">Wallet Logs</h1>
          </Colxx>
          <Colxx xxs="12" md="6" style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '1rem' }}>
            <Button
              color="primary"
              size="sm"
              onClick={exportWalletLogsCSV}
              disabled={!groupedData || groupedData.length === 0 || loading}
              style={{ whiteSpace: 'nowrap' }}
            >
              Export CSV
            </Button>
            <div style={{
              display: 'inline-block',
              position: 'relative'
            }}>
              <DatePicker
                selected={startDate}
                onChange={handleDateRangeChange}
                startDate={startDate}
                endDate={endDate}
                selectsRange
                dateFormat="MMM dd, yyyy"
                placeholderText="Select date range"
                popperClassName="filter-datepicker"
                popperPlacement="bottom-start"
                showPopperArrow={false}
                maxDate={new Date()}
                filterDate={(date) => {
                  // If start date is selected, limit end date to 30 days from start
                  if (startDate && !endDate) {
                    const maxEndDate = moment(startDate).add(30, 'days').toDate()
                    return date <= maxEndDate && date <= new Date()
                  }
                  // If both dates are selected, allow any date within range
                  if (startDate && endDate) {
                    return date <= new Date()
                  }
                  return date <= new Date()
                }}
                className="form-control"
                style={{
                  padding: '0.5rem',
                  fontSize: '0.875rem',
                  borderRadius: '0.375rem',
                  border: `1px solid ${dateRangeError ? '#e74c3c' : '#d1d5db'}`,
                  minWidth: '240px',
                  cursor: 'pointer'
                }}
                customInput={
                  <input
                    style={{
                      padding: '0.5rem 1rem',
                      fontSize: '0.875rem',
                      borderRadius: '0.375rem',
                      border: `1px solid ${dateRangeError ? '#e74c3c' : '#d1d5db'}`,
                      minWidth: '260px',
                      cursor: 'pointer',
                      backgroundColor: '#fff',
                      width: '100%'
                    }}
                    value={getDateRangeDisplay()}
                    readOnly
                  />
                }
              />
            </div>
          </Colxx>
        </Row>
      </div>

      {/* Analytics Section */}
      <Row className="mt-2">
        <Colxx xxs="12" sm="6" md="4" lg="2" className="mb-4">
          <StatCard
            title="Total Users"
            value={analytics.totalUsers}
            color="#3b82f6"
            formatCurrency={false}
          />
        </Colxx>
        <Colxx xxs="12" sm="6" md="4" lg="2" className="mb-4">
          <StatCard
            title="Total Transactions"
            value={analytics.totalTransactions}
            color="#10b981"
            formatCurrency={false}
          />
        </Colxx>
        <Colxx xxs="12" sm="6" md="4" lg="2" className="mb-4">
          <StatCard
            title="Total"
            value={analytics.total}
            color="#6366f1"
            formatCurrency={true}
          />
        </Colxx>
        <Colxx xxs="12" sm="6" md="4" lg="2" className="mb-4">
          <StatCard
            title="Total Credit"
            value={analytics.totalCredit}
            color="#8b5cf6"
            formatCurrency={true}
          />
        </Colxx>
        <Colxx xxs="12" sm="6" md="4" lg="2" className="mb-4">
          <StatCard
            title="Total Cashback"
            value={analytics.totalCashback}
            color="#f59e0b"
            formatCurrency={true}
          />
        </Colxx>
        <Colxx xxs="12" sm="6" md="4" lg="2" className="mb-4">
          <StatCard
            title="Total Vouchers"
            value={analytics.totalVouchers}
            color="#ec4899"
            formatCurrency={true}
          />
        </Colxx>
        <Colxx xxs="12" sm="6" md="4" lg="2" className="mb-4">
          <StatCard
            title="Total Compensation"
            value={analytics.totalCompensation}
            color="#a855f7"
            formatCurrency={true}
          />
        </Colxx>
        <Colxx xxs="12" sm="6" md="4" lg="2" className="mb-4">
          <StatCard
            title="Redeemed Credit"
            value={analytics.redeemedCredit}
            color="#ef4444"
            formatCurrency={true}
          />
        </Colxx>
        <Colxx xxs="12" sm="6" md="4" lg="2" className="mb-4">
          <StatCard
            title="Redeemed Cashback"
            value={analytics.redeemedCashback}
            color="#dc2626"
            formatCurrency={true}
          />
        </Colxx>
        <Colxx xxs="12" sm="6" md="4" lg="2" className="mb-4">
          <StatCard
            title="Redeemed Vouchers"
            value={analytics.redeemedVouchers}
            color="#b91c1c"
            formatCurrency={true}
          />
        </Colxx>
        <Colxx xxs="12" sm="6" md="4" lg="2" className="mb-4">
          <StatCard
            title="Redeemed Compensation"
            value={analytics.redeemedCompensation}
            color="#991b1b"
            formatCurrency={true}
          />
        </Colxx>
        <Colxx xxs="12" sm="6" md="4" lg="2" className="mb-4">
          <StatCard
            title="Redeemed BOGO Amount"
            value={analytics.redeemedBogoAmount}
            color="#0891b2"
            formatCurrency={true}
          />
        </Colxx>
        <Colxx xxs="12" sm="6" md="4" lg="2" className="mb-4">
          <StatCard
            title="Total Transferred"
            value={analytics.totalTransferred}
            color="#2ecc71"
            formatCurrency={true}
          />
        </Colxx>
        {/* <Colxx xxs="12" sm="6" md="4" lg="2" className="mb-4">
          <StatCard
            title="Transferred Count"
            value={analytics.transferredCount}
            color="#27ae60"
            formatCurrency={false}
          />
        </Colxx> */}
      </Row>

      {/* Table Section */}
      <Row>
        <Colxx xxs="12" className='pb-5'>

          {filteredData && filteredData.length > 0 ? (
            <ReactTable
              data={filteredData}
              columns={dataTableColumns()}
              className='custuomReactTable'
              pageSize={filteredData.length < 50 ? filteredData.length : 50}
              showPageJump={false}
              minRows={0}
              PaginationComponent={DataTablePagination}
              showPageSizeOptions={false}
              defaultSorted={[
                {
                  id: 'lastLogDate',
                  desc: true
                }
              ]}
            />
          ) : !loading ? (
            <div className="d-flex flex-row mb-3 pb-3 text-center justify-content-center">
              <div className='font-weight-bold'>No wallet logs found!</div>
            </div>
          ) : null}
        </Colxx>
      </Row>
      {selectedCustomer && (
        <Customer
          customer={selectedCustomer}
          dismiss={() => setSelectedCustomer(null)}
        />
      )}
    </Fragment>
  );
};

export default WalletLogs;
